import { useState } from '#imports'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { capitalize } from '@backmarket/utils/string/capitalize'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES as SWAP_MODAL_NAMES } from '~/scopes/buyback/constants'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'
import { useSwapModalStore } from '~/scopes/buyback/swap/stores/useSwapModalStore'
import { useAddToCartOverlay } from '~/scopes/product-core/composables/stores/useAddToCartOverlay/useAddToCartOverlay'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import { MODAL_NAMES as MODAL_NAMES_JP } from '~/scopes/product-core/pages/ProductDetailPage/jp/jp.constants'
import type { PartnerNames } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/types'
import type { SearchTrackingData } from '~/scopes/product-list/composables/useProductsSearch/useProductsSearch.types'

import { getResolvedTelcoOffer } from './getResolvedTelcoOffer'
import { TOAST_PARAMS_BY_ERROR_TYPE } from './useAddToCart.constants'

type ErrorWithType = {
  data?: {
    type: string
  }
}

function hasType(error: unknown): error is ErrorWithType {
  return (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'type' in error.data
  )
}

type AddToCartPayload = {
  name: string
  price: string | number
  currency: string
  id?: string | number
  listingId?: string | number
  uuid?: string
  category?: string | number
  list?: string
  merchantId?: string
  quantity?: number
  variant?: number
  brand?: string
  color?: string
  model?: string
  shippingDelay?: string | number
  specialOfferType?: string | number
  isFlashSales?: boolean
  isSellerFlashSales?: boolean
  averageRate?: number
  numberTotalReviews?: number
  dealType?: string
  discount?: number
  [extra: string]: unknown
}

function toGa4AddToCartItem(tracking: AddToCartPayload, locationId: string) {
  const price =
    typeof tracking.price === 'number'
      ? tracking.price
      : parseFloat(tracking.price)

  return {
    item_id: String(tracking.id ?? ''),
    item_uuid: tracking.uuid ?? '',
    item_name: tracking.name,
    item_brand: capitalize(tracking.brand ?? ''),
    item_category:
      tracking.category != null ? String(tracking.category) : undefined,
    price,
    quantity: 1,
    affiliation: tracking.merchantId,
    discount: tracking.discount,
    item_list_id: tracking.list,
    item_list_name: tracking.list,
    item_variant: tracking.variant,
    item_variant3: tracking.model,
    item_variant6: tracking.color,
    location_id: locationId,
    google_business_vertical: 'retail',
    index: 0,
  }
}

export const useAddToCart = () => {
  const { openErrorToast } = useTheToast()
  const {
    trackAddToCart,
    trackAddToCartGa4,
    trackErrors,
    trackSearchAnalytics,
    trackClick,
  } = useTracking()
  const { countryCode } = useMarketplace().market
  const { mobilePlan } = useUrlParams()
  const i18n = useI18n()
  const { hasBuybackOffer } = useBuybackOffer()
  const { isSwapEnabled } = useSwapEnabled()

  const swapModalStore = useSwapModalStore()
  const experiments = useExperiments()
  const userStore = useUserStore()
  const { openAddToCartOverlay } = useAddToCartOverlay()
  const visitorId = useVisitorId()
  const $httpFetch = useHttpFetch()

  const addToCart = async ({
    listingId,
    listingPublicId,
    tracking,
    partnerPromoCodes,
    isTradeInEligible,
    promoCodes,
    telcoSelection,
  }: {
    listingId?: number | null
    listingPublicId: string
    tracking: AddToCartPayload
    partnerPromoCodes: GetProductResponse['includedServiceOffers']['partnerPromoCodes']
    isTradeInEligible: boolean
    promoCodes?: GetProductResponse['promoCodes']
    /**
     * Cart-relevant descriptor from `useTelcoPicker.telcoCartSelection`.
     *
     * - `isPickerActive: true` → picker mode (≥2 offers, US, unlocked, non-referral,
     *   flag on). The buyer's `selectedPartnerName` is authoritative:
     *   - non-null → post PARTNER_PROMO_CODE iff the matched backend row has
     *     `assignment === true`.
     *   - null (None) → no telco service posted.
     * - `isPickerActive: false` → legacy / single-offer / referral mode. Post
     *   PARTNER_PROMO_CODE for `partnerPromoCodes[0]` iff its `assignment === true`.
     * - `undefined` → same as `isPickerActive: false` (caller did not supply it).
     */
    telcoSelection?: {
      isPickerActive: boolean
      selectedPartnerName: PartnerNames | null
    }
  }) => {
    trackAddToCart({
      ...tracking,
      quantity: 1,
    })
    const ga4Item = toGa4AddToCartItem(tracking, countryCode)
    trackAddToCartGa4({
      zone: 'product_block_primary',
      currency: tracking.currency,
      hasTradeIn: hasBuybackOffer.value,
      value: ga4Item.price * ga4Item.quantity,
      items: [ga4Item],
    })

    const searchTrackingData = useState<SearchTrackingData>('search-query')

    try {
      // This fields is optional in the new endpoint but this should no happen
      if (!listingId) {
        throw new Error()
      }

      await $httpFetch('/bm/cart/add_product', {
        method: 'POST',
        body: {
          listing_id: listingId,
          searchRelatedQuery: {
            id: searchTrackingData.value?.id ?? null,
            index: searchTrackingData.value?.index ?? null,
          },
        },
      })

      const servicesToAdd = []

      if (mobilePlan.value) {
        const addMobilePlan = $httpFetch('/bm/cart/services', {
          method: 'POST',
          body: {
            type: 'MOBILE_PLAN',
            listingPublicId,
            offerId: mobilePlan.value,
          },
        })

        servicesToAdd.push(addMobilePlan)
      }

      // Telco partner resolution — single decision point. See
      // `getResolvedTelcoOffer` for the trust boundary and the two modes.
      const resolvedTelcoOffer = getResolvedTelcoOffer({
        partnerPromoCodes,
        telcoSelection,
      })

      if (resolvedTelcoOffer) {
        const addTelcoPlan = $httpFetch('/bm/cart/services', {
          method: 'POST',
          body: {
            type: 'PARTNER_PROMO_CODE',
            listingPublicId,
            offerId: resolvedTelcoOffer.offerId,
            partnerName: resolvedTelcoOffer.partnerName,
          },
        })

        servicesToAdd.push(addTelcoPlan)
      }

      const flashSalesPromoCode = promoCodes?.find(
        (promoCode) => !!promoCode.isFlashSale,
      )?.token

      if (
        flashSalesPromoCode &&
        experiments['experiment.flashSales'] === 'enabled' &&
        experiments['experiment.promoCodeInCart'] !== 'disabled'
      ) {
        await $httpFetch('/bm/cart/services', {
          method: 'POST',
          body: {
            type: 'PROMO_CODE',
            listingPublicId,
            promoCodeToken: flashSalesPromoCode,
          },
        })
      }

      await Promise.all(servicesToAdd)

      // Track the telco plan only AFTER the add-to-cart services have resolved,
      // mirroring trackAddToCart success timing.
      if (resolvedTelcoOffer) {
        trackClick({
          zone: 'add_to_cart',
          name: 'telco_plan_in_cart',
          telcoPlanName: resolvedTelcoOffer.partnerName.toLowerCase(),
        })
      }

      if (
        isSwapEnabled &&
        !hasBuybackOffer.value &&
        swapModalStore.showAddToCartModal &&
        isTradeInEligible
      ) {
        openModal(SWAP_MODAL_NAMES.SWAP_ATC)
      } else {
        openModal(MODAL_NAMES_JP.ADD_TO_CART)
        openAddToCartOverlay()
      }

      if (
        searchTrackingData.value?.id &&
        searchTrackingData.value.productObjectId
      ) {
        trackSearchAnalytics({
          type: 'conversion',
          objectId: searchTrackingData.value.productObjectId,
          searchQueryID: searchTrackingData.value.id ?? '',
          tokenId: visitorId,
          index: searchTrackingData.value.index ?? '',
        })
      }
      await userStore.fetchUser()
    } catch (error) {
      const isIncompatibilityError =
        hasType(error) &&
        (error.data?.type ===
          '/errors/cart/add-service-to-bag/mobile-plan-incompatible-with-promo-code' ||
          error.data?.type ===
            '/errors/cart/add-service-to-bag/promo-code-incompatible-with-mobile-plan')

      if (isIncompatibilityError && listingId) {
        await $httpFetch('/bm/cart/update_quantity', {
          method: 'POST',
          body: {
            action: 'delete',
            listing_id: String(listingId),
            new_quantity: 0,
            listing_price: 0,
          },
        })
      }

      trackErrors({
        zone: 'add_to_cart',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (error as any)?.data?.detail ?? 'unknown_error',
      })

      const errorType = hasType(error) ? error.data?.type : undefined

      if (errorType && TOAST_PARAMS_BY_ERROR_TYPE[errorType]) {
        openErrorToast({
          title: i18n(TOAST_PARAMS_BY_ERROR_TYPE[errorType].title),
          content: i18n(TOAST_PARAMS_BY_ERROR_TYPE[errorType].content),
        })
      } else {
        // Default error toast if we cannot match the error response to a mapped type
        openErrorToast()
      }
    }
  }

  return {
    addToCart,
  }
}
